import axios from 'axios';

import { rawApi } from '@/apis/rawApi';

export const uploadFile = async (signedUrl: string, file: File) => {
  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
  return signedUrl.split('?')[0];
};

function httpStatusFromUnknown(e: unknown): number | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const s = (e as { status?: unknown }).status;
  if (typeof s === 'number' && Number.isFinite(s)) return s;
  if (typeof s === 'string') {
    const n = parseInt(s, 10);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function errorTextFromUnknown(e: unknown): string {
  if (!e || typeof e !== 'object') return '';
  const o = e as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof o.data === 'string') parts.push(o.data);
  else if (o.data && typeof o.data === 'object' && 'message' in o.data) {
    parts.push(String((o.data as { message: unknown }).message));
  }
  if (typeof o.error === 'string') parts.push(o.error);
  if (typeof o.message === 'string') parts.push(o.message);
  return parts.join(' ');
}

/** RTK Query / axios-style error from signed-url when S3 env is missing */
export function isS3NotConfiguredError(e: unknown): boolean {
  if (httpStatusFromUnknown(e) !== 503) return false;
  const text = errorTextFromUnknown(e);
  if (text.toLowerCase().includes('not configured')) return true;
  try {
    return JSON.stringify(e).toLowerCase().includes('not configured');
  } catch {
    return false;
  }
}

/** Authenticated dev fallback when API has STORAGE_LOCAL_DEV=1 */
export async function uploadFileLocalDev(
  file: File,
  folderName: string
): Promise<string> {
  const formData = new FormData();
  formData.append('folderName', folderName);
  formData.append('file', file);
  const res = await rawApi.post<{ url: string }>(
    '/global/storage/upload-local',
    formData
  );
  if (!res.data?.url) throw new Error('Upload failed: no URL returned');
  return res.data.url;
}

/**
 * Try S3 presigned PUT; on 503 "not configured", POST to local disk upload
 * (enabled on API when S3 is missing and NODE_ENV is not production, or STORAGE_LOCAL_DEV=1).
 */
export async function uploadStorageFile(
  file: File,
  folderName: string,
  getSignedUrl: () => Promise<string>
): Promise<string> {
  try {
    const signedUrl = await getSignedUrl();
    return await uploadFile(signedUrl, file);
  } catch (e) {
    if (isS3NotConfiguredError(e)) {
      try {
        return await uploadFileLocalDev(file, folderName);
      } catch {
        throw new Error(
          'File upload is unavailable: S3 is not configured on the server. ' +
            'Add AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, AWS_REGION (or AWS_BUCKET_REGION), and S3_BUCKET_NAME (or AWS_BUCKET_NAME), ' +
            'or enable local uploads: run the API with S3 unset and NODE_ENV not set to production, or set STORAGE_LOCAL_DEV=1 in the API .env.'
        );
      }
    }
    throw e;
  }
}
